import { v4 as uuid } from 'uuid';
import { runInAction } from 'mobx';
import isEqual from 'lodash/isEqual';
import type { InputBaseModel, NodeBaseModel } from '../models';
import {
    contractInputReference,
    contractInputReferences,
    contractReferenceKey as key,
    contractRecordReference
} from './ContractDraftScope';
import type { ContractChangeReferenceDto, ContractSaveIntentDto } from './types';

export interface ContractCause {
    readonly rootId: string;
    readonly trigger: ContractChangeReferenceDto;
}

type Draft = { kind: 'direct'; id: string; target: ContractChangeReferenceDto; revision: number };
type Effect = { kind: 'automatic'; target: ContractChangeReferenceDto; cause: ContractCause; revision: number };
// Owns unsaved causal hints and save acknowledgments only.
export default class ContractDraftHistory {
    private revision = 0;
    private suspended = 0;
    private currentCause?: ContractCause;
    private changes = new Map<string, Draft | Effect>();
    private manualTargets = new Set<string>();
    private submissionOrder = 0;
    private submissions = new Map<string, { order: number; changes: Map<string, number> }>();
    private deferredTargets = new Set<string>();

    constructor(private readonly nodes: () => NodeBaseModel[]) {}

    private get directs() {
        return Array.from(this.changes.values()).filter((change): change is Draft => change.kind === 'direct');
    }

    private get effects() {
        return Array.from(this.changes.values()).filter((change): change is Effect => change.kind === 'automatic');
    }

    reference(input: InputBaseModel) {
        return contractInputReference(input);
    }

    capture(target: ContractChangeReferenceDto): ContractCause {
        const targetKey = key(target);
        const existing = this.changes.get(targetKey);
        const draft: Draft = {
            kind: 'direct',
            id: existing?.kind === 'direct' ? existing.id : uuid(),
            target,
            revision: ++this.revision
        };
        this.changes.set(targetKey, draft);
        return { rootId: draft.id, trigger: target };
    }

    editInputs(inputs: InputBaseModel[], apply: (input: InputBaseModel, index: number, cause: ContractCause) => void) {
        // Reserve every manual target before synchronous notifications run. This
        // protection ends with the event; later automatic writes own their values.
        const previous = this.manualTargets;
        this.manualTargets = new Set([...previous, ...inputs.map(input => key(this.reference(input)))]);
        const causes = inputs.map(input => this.capture(this.reference(input)));
        try {
            inputs.forEach((input, index) => this.withCause(causes[index], () => apply(input, index, causes[index])));
        } finally {
            this.manualTargets = previous;
        }
    }

    causeFor(input: InputBaseModel): ContractCause | undefined {
        if (this.suspended) {
            return undefined;
        }
        const target = this.reference(input);
        const change = this.changes.get(key(target));
        return (
            this.currentCause ??
            (change
                ? { rootId: change.kind === 'direct' ? change.id : change.cause.rootId, trigger: target }
                : undefined)
        );
    }

    withCause<T>(cause: ContractCause | undefined, operation: () => T): T {
        const previous = this.currentCause;
        this.currentCause = cause;
        try {
            return operation();
        } finally {
            this.currentCause = previous;
        }
    }

    withoutCapture<T>(operation: () => T): T {
        ++this.suspended;
        try {
            return runInAction(operation);
        } finally {
            --this.suspended;
        }
    }

    observe(input: InputBaseModel, cause?: ContractCause) {
        const target = this.reference(input);
        // An unchanged intermediate still connects a chain, but a lookup alone
        // cannot replace the actor who supplied a retained manual value.
        if (this.changes.get(key(target))?.kind !== 'direct') {
            this.recordEffect(target, cause);
        }
    }

    get pendingTargets(): ContractChangeReferenceDto[] {
        return Array.from(this.changes.values(), change => change.target);
    }

    hasTarget(target: ContractChangeReferenceDto) {
        return this.changes.has(key(target));
    }

    clear() {
        this.changes.clear();
        this.submissions.clear();
        this.deferredTargets.clear();
    }

    setDeferredTargets(targets: ContractChangeReferenceDto[]) {
        this.deferredTargets = new Set(targets.map(key));
    }

    changed(input: InputBaseModel, previousValue: unknown, notify: () => void) {
        if (this.suspended) {
            return notify();
        }
        const target = this.reference(input);
        const cause = this.currentCause;
        if (!isEqual(previousValue, input.value)) {
            this.recordEffect(target, cause);
        }
        this.withCause(cause ? { rootId: cause.rootId, trigger: target } : undefined, notify);
    }

    snapshot(): ContractSaveIntentDto {
        const saveId = uuid();
        const directs = this.directs.filter(direct => !this.deferredTargets.has(key(direct.target)));
        const rootIds = new Set(directs.map(direct => direct.id));
        const effects = this.effects.filter(
            effect => rootIds.has(effect.cause.rootId) && !this.deferredTargets.has(key(effect.target))
        );
        this.submissions.set(saveId, {
            order: ++this.submissionOrder,
            changes: new Map(
                Array.from(this.changes)
                    .filter(([target]) => !this.deferredTargets.has(target))
                    .map(([target, change]) => [target, change.revision])
            )
        });
        return {
            saveId,
            directChanges: directs.map(direct => ({
                id: direct.id,
                target: direct.target,
                automaticChanges: effects
                    .filter(effect => effect.cause.rootId === direct.id)
                    .map(effect => ({ target: effect.target, triggeredBy: effect.cause.trigger }))
            }))
        };
    }

    acknowledge(saveId?: string | null) {
        if (!saveId) {
            return;
        }
        const submission = this.submissions.get(saveId);
        if (!submission) {
            return;
        }
        this.changes.forEach((change, target) => {
            const submittedRevision = submission.changes.get(target);
            if (submittedRevision !== undefined && change.revision <= submittedRevision) {
                this.changes.delete(target);
            }
        });
        this.submissions.forEach((value, id) => {
            if (value.order <= submission.order) {
                this.submissions.delete(id);
            }
        });
    }

    get hasPendingStructure() {
        return this.directs.some(draft => !draft.target.nodeId);
    }

    // Comparison rebuilds execute the same availability predicates without saving
    // the contract. Carry those consequences into the later comparison Save.
    captureRebuild(nextNodes: NodeBaseModel[]) {
        const index = (nodes: NodeBaseModel[]) =>
            new Map(
                contractInputReferences(nodes).map(({ input, reference, key: targetKey }) => [
                    targetKey,
                    { input, target: reference }
                ])
            );
        const before = index(this.nodes());
        const after = index(nextNodes);
        const entries = new Map([...before, ...after]);
        const owners = [...new Set([...before.values(), ...after.values()].map(entry => entry.input.ownerNode))];
        const capturedEffects = new Set(this.effects.map(effect => key(effect.target)));
        // Resolve against both graphs: an unavailable field still carries its declared cause.
        const availabilityCauses = (entry: { target: ContractChangeReferenceDto }) =>
            owners
                .filter(node => node.id === entry.target.nodeId)
                .flatMap(node => node.inputAvailability[entry.target.itemId] ?? [])
                .map(trigger => ({
                    nodeId: trigger.contractNodeId ?? entry.target.nodeId,
                    itemId: trigger.inputId,
                    layerId: trigger.contractNodeId ? null : entry.target.layerId,
                    sectionId: trigger.contractNodeId ? null : entry.target.sectionId
                }));
        let previousRevision: number;
        do {
            previousRevision = this.revision;
            entries.forEach((entry, targetKey) => {
                if (capturedEffects.has(targetKey)) {
                    return;
                }
                const oldInput = before.get(targetKey)?.input;
                const newInput = after.get(targetKey)?.input;
                if (isEqual(oldInput?.dto.value ?? null, newInput?.dto.value ?? null)) {
                    return;
                }
                const parents = entry.input.parentInputIds;
                const existing = this.changes.get(targetKey);
                const candidates = [
                    ...this.directs.map(draft => ({ target: draft.target, rootId: draft.id })),
                    ...this.effects.map(effect => ({
                        target: effect.target,
                        rootId: effect.cause.rootId
                    }))
                ].filter(candidate => {
                    const sameGroup =
                        candidate.target.nodeId === entry.target.nodeId &&
                        candidate.target.layerId === entry.target.layerId &&
                        candidate.target.sectionId === entry.target.sectionId;
                    return (
                        !this.isCausedBy(candidate.target, entry.target) &&
                        ((sameGroup && parents.includes(candidate.target.itemId)) ||
                            ((!oldInput || !newInput) &&
                                availabilityCauses(entry).some(cause => key(cause) === key(candidate.target))))
                    );
                });
                // Several paths from one root are not several manual edits. Keep
                // the nearest parent in a chain, regardless of input ordering.
                const immediate = candidates.filter(
                    candidate =>
                        !candidates.some(
                            other => other !== candidate && this.isCausedBy(other.target, candidate.target)
                        )
                );
                if (new Set(candidates.map(candidate => candidate.rootId)).size === 1 && immediate.length === 1) {
                    const cause = { rootId: immediate[0].rootId, trigger: immediate[0].target };
                    if (
                        existing?.kind === 'automatic' &&
                        existing.cause.rootId === cause.rootId &&
                        key(existing.cause.trigger) === key(cause.trigger)
                    ) {
                        return;
                    }
                    this.changes.set(targetKey, {
                        kind: 'automatic',
                        target: entry.target,
                        cause,
                        revision: ++this.revision
                    });
                } else if (existing?.kind === 'automatic' && this.changes.delete(targetKey)) {
                    ++this.revision;
                }
            });
        } while (this.revision !== previousRevision);
    }

    // Comparison Save applies a user-selected draft to the live contract. Retain
    // its original roots/edges, then capture changed values with no earlier hint.
    captureMerge(nextNodes: NodeBaseModel[], comparisonHistory?: ContractDraftHistory) {
        if (comparisonHistory) {
            const roots = new Map<string, string>();
            comparisonHistory.directs.forEach(draft => roots.set(draft.id, this.capture(draft.target).rootId));
            comparisonHistory.effects.forEach(effect => {
                const rootId = roots.get(effect.cause.rootId);
                if (rootId) {
                    this.changes.set(key(effect.target), {
                        ...effect,
                        cause: { ...effect.cause, rootId },
                        revision: ++this.revision
                    });
                }
            });
        }
        const index = (nodes: NodeBaseModel[]) => {
            const values = new Map<string, { reference: ContractChangeReferenceDto; value: unknown }>();
            contractInputReferences(nodes).forEach(({ input, reference, key: target }) => {
                // Preserve the original first-match behavior for duplicate references.
                if (!values.has(target)) {
                    values.set(target, { reference, value: input.dto.value });
                }
            });
            return values;
        };
        const before = index(this.nodes());
        const after = index(nextNodes);
        const recordKey = (reference: ContractChangeReferenceDto) =>
            key(contractRecordReference(reference) ?? reference);
        const oldRecords = new Set(
            [...before.values()].filter(entry => entry.reference.recordId).map(entry => recordKey(entry.reference))
        );
        const newRecords = new Set(
            [...after.values()].filter(entry => entry.reference.recordId).map(entry => recordKey(entry.reference))
        );
        const changedRecords = new Set(
            [...oldRecords, ...newRecords].filter(id => oldRecords.has(id) !== newRecords.has(id))
        );
        const seen = new Set<string>();
        new Map([...before, ...after]).forEach((entry, entryKey) => {
            let reference = entry.reference;
            if (reference.recordId && changedRecords.has(recordKey(reference))) {
                reference = contractRecordReference(reference) ?? reference;
            } else {
                const oldValue = before.get(entryKey)?.value;
                const newValue = after.get(entryKey)?.value;
                if (isEqual(oldValue ?? null, newValue ?? null)) {
                    return;
                }
            }
            const target = key(reference);
            if (!seen.has(target) && !this.changes.has(target)) {
                this.capture(reference);
            }
            seen.add(target);
        });
    }

    private recordEffect(target: ContractChangeReferenceDto, cause?: ContractCause) {
        if (!this.suspended && cause && key(target) !== key(cause.trigger) && !this.manualTargets.has(key(target))) {
            this.changes.set(key(target), { kind: 'automatic', target, cause, revision: ++this.revision });
        }
    }

    private isCausedBy(target: ContractChangeReferenceDto, ancestor: ContractChangeReferenceDto): boolean {
        const ancestorKey = key(ancestor);
        const visited = new Set<string>();
        let targetKey = key(target);
        while (!visited.has(targetKey)) {
            if (targetKey === ancestorKey) {
                return true;
            }
            visited.add(targetKey);
            const effect = this.changes.get(targetKey);
            if (effect?.kind !== 'automatic') {
                return false;
            }
            targetKey = key(effect.cause.trigger);
        }
        return false;
    }
}
